/* @jsx h */
import { defineComponent, h } from "vue";

export type HeroTone = "default" | "error" | "success";

const TONE = {
  default: { glow: "bg-primary/25", icon: "text-primary", ring: "border-primary/40" },
  error: { glow: "bg-error/25", icon: "text-error", ring: "border-error/40" },
  success: { glow: "bg-success/25", icon: "text-success", ring: "border-success/40" },
} as const;

/**
 * Large glowing glyph at the top of each setup screen, in the spirit of a
 * watch pairing flow. Animating adds rings that pulse outward.
 */
export const HeroIcon = defineComponent<{
  icon: string;
  isAnimating?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
}>({
  name: "PrinterSetupHeroIcon",
  props: ["icon", "isAnimating", "isError", "isSuccess"],
  setup(props) {
    return () => {
      const tone =
        TONE[props.isError ? "error" : props.isSuccess ? "success" : "default"];
      return (
        <div class="relative mb-4 flex h-20 w-20 items-center justify-center">
          <div class={["absolute inset-1 rounded-full blur-xl", tone.glow]} />
          {props.isAnimating && (
            <>
              <div
                class={[
                  "absolute inset-0 rounded-full border animate-ping",
                  tone.ring,
                ]}
              />
              <div
                class={[
                  "absolute -inset-2 rounded-full border opacity-50 animate-pulse",
                  tone.ring,
                ]}
              />
            </>
          )}
          <div class="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-b from-base-100 to-base-300 shadow-lg ring-1 ring-base-content/10">
            <span class={["material-symbols-outlined text-4xl", tone.icon]}>
              {props.icon}
            </span>
          </div>
        </div>
      );
    };
  },
});

/**
 * Renders a translated template, showing each `{placeholder}` that has a
 * value in `values` as prominent text.
 */
export const RichText = defineComponent<{
  text: string;
  values?: Record<string, string>;
}>({
  name: "PrinterSetupRichText",
  props: ["text", "values"],
  setup(props) {
    return () => (
      <>
        {props.text.split(/(\{\w+\})/).map((part) => {
          const value = props.values?.[part.slice(1, -1)];
          if (!/^\{\w+\}$/.test(part) || value === undefined) return part;
          return <span class="font-semibold text-accent">{value}</span>;
        })}
      </>
    );
  },
});

export const StepHeader = defineComponent<{
  title: string;
  text?: string;
  values?: Record<string, string>;
}>({
  name: "PrinterSetupStepHeader",
  props: ["title", "text", "values"],
  setup(props) {
    return () => (
      <div class="mb-4 max-w-sm">
        <h2 class="text-2xl font-bold tracking-tight text-accent">
          {props.title}
        </h2>
        {props.text && (
          <p class="mt-1.5 text-sm leading-relaxed text-base-content/70">
            <RichText text={props.text} values={props.values} />
          </p>
        )}
      </div>
    );
  },
});
